import * as React from 'react';
import {Dispatch} from 'redux';
import {connect} from 'react-redux';
import isEqual = require('lodash/isEqual'); // tslint:disable-line: no-require-imports
import {configEditorDispatchToPropsObject, globalDispatchToPropsObject} from '../../store';

import AssetStatus from './AssetStatus';
import AssetList from './AssetList';
import AssetEditor from './AssetEditor';
import ModifyAssetsModal from './ModifyAssetsModal';
import AssetLoadSuccessModal from './AssetLoadSuccessModal';
import AssetLoadFailureModal from './AssetLoadFailureModal';
import ConfirmLoadAssetsModal from './ConfirmLoadAssetsModal';
import ConfirmModal from './ConfirmModal';
import {SplitPaneVertical} from '../util';
import EditorHelp from './EditorHelp';
import {renewAuthenticationIfAboutToExpire} from '../auth';
import {ModificationData} from './_util';
import {EXTERNAL_SERVICE_INTEGRATIONS_ENABLED} from '../feature-flags';

interface StateProps {
	user: UserState;
	files: ConfigEditor.AssetFiles;
	editing: ConfigEditor.AssetEditorState;
	isModified: boolean;
	isPending: boolean;
	loadedOnce: boolean;
	loadResult: ConfigEditor.DeployResponse;
	validation?: ConfigEditor.AssetFiles;
}

interface DispatchProps {
	importAssets(zipFile: File): Promise<ConfigEditor.AssetFiles>;
	validateAsset(): Promise<ConfigEditor.AssetFiles>;
	validateAssets(): Promise<ConfigEditor.AssetFiles>;
	initializeAssets(): void;
	addAssets(modifying: ConfigEditor.ModificationEntity, modification: ConfigEditor.AssetsModification): void;
	removeAssets(modifying: ConfigEditor.ModificationEntity, modification: ConfigEditor.AssetsModification): void;
	exportAssets(): void;
	loadAssets(
		action: ConfigEditor.LoadAssetsActionType,
		externalServiceIntegrations?: ExternalServiceIntegrationRequest[],
	): void;
	resetAssets(): void;
	changeAsset(contents: string): void;
	switchToAsset(path: string): void;
	saveAsset(): void;
	revertAsset(): void;
	openSite(): void;
	renewAuth(authToken: string): Promise<AuthenticateResponse>;
	clearAuthIfExpired(): void;
}

type AssetsProps = StateProps & DispatchProps;

interface AssetsState {
	confirmSwitchToAsset?: {
		path: string;
	};
	confirmLoadAssets?: boolean;
	confirmModifyAssets: boolean;
	confirmResetAssets?: boolean;
	confirmReinitializeAssets?: boolean;
	fontSizeAdjustment: number;
}

function isModified(configEditorState: ConfigEditor.AppState) {
	if (!configEditorState.assets.editing) {
		return false;
	}
	const {path, contents}: ConfigEditor.AssetEditorState = configEditorState.assets.editing;
	return configEditorState.assets.pending[path] !== contents;
}

function mapStateToProps({configEditor, global}: StudioState): StateProps {
	return {
		user: global.user,
		files: configEditor.assets.pending || {},
		editing: configEditor.assets.editing,
		isModified: isModified(configEditor),
		isPending: !isEqual(configEditor.assets.pending, configEditor.assets.loaded),
		loadedOnce: !!configEditor.assets.loaded,
		loadResult: configEditor.assets.result,
		validation: configEditor.assets.validation,
	};
}

function mapDispatchToProps(dispatch: Dispatch<StudioState>): DispatchProps {
	const {
		importAssets,
		validateAsset,
		validateAssets,
		initializeAssets,
		addAssets,
		removeAssets,
		exportAssets,
		loadAssets,
		resetAssets,
		changeAsset,
		switchToAsset,
		saveAsset,
		revertAsset,
		openSite,
	} = configEditorDispatchToPropsObject;
	const {renewAuthentication, clearAuthenticationIfExpired} = globalDispatchToPropsObject;
	return {
		importAssets: zipFile => dispatch(importAssets(zipFile)),
		validateAsset: () => dispatch(validateAsset()),
		validateAssets: () => dispatch(validateAssets()),
		initializeAssets: () => dispatch(initializeAssets()),
		addAssets: (modifying, modification) => dispatch(addAssets(modifying, modification)),
		removeAssets: (modifying, modification) => dispatch(removeAssets(modifying, modification)),
		exportAssets: () => dispatch(exportAssets()),
		loadAssets: (action, externalServiceIntegrations) => dispatch(loadAssets(action, externalServiceIntegrations)),
		resetAssets: () => dispatch(resetAssets()),
		changeAsset: contents => dispatch(changeAsset(contents)),
		switchToAsset: path => dispatch(switchToAsset(path)),
		saveAsset: () => dispatch(saveAsset()),
		revertAsset: () => dispatch(revertAsset()),
		openSite: () => dispatch(openSite()),
		renewAuth: authToken => dispatch(renewAuthentication(authToken)),
		clearAuthIfExpired: () => dispatch(clearAuthenticationIfExpired()),
	};
}

class ConfigEditor extends React.Component<AssetsProps, AssetsState> {

	public state: AssetsState = {
		confirmSwitchToAsset: null,
		confirmLoadAssets: false,
		confirmModifyAssets: false,
		confirmResetAssets: false,
		confirmReinitializeAssets: false,
		fontSizeAdjustment: 0,
	};

	constructor(props: AssetsProps, context: any) {
		super(props, context);
		this.handleSwitchToAsset = this.handleSwitchToAsset.bind(this);
		this.handleLoadAssets = this.handleLoadAssets.bind(this);
		this.handleModifyAssets = this.handleModifyAssets.bind(this);
		this.handleResetAssets = this.handleResetAssets.bind(this);
		this.handleReinitializeAssets = this.handleReinitializeAssets.bind(this);
		this.handleSaveAsset = this.handleSaveAsset.bind(this);
		this.handleRevertAsset = this.handleRevertAsset.bind(this);
		this.handleImportAssets = this.handleImportAssets.bind(this);
		this.handleSwitchToAssetConfirm = this.handleSwitchToAssetConfirm.bind(this);
		this.handleLoadAssetsConfirm = this.handleLoadAssetsConfirm.bind(this);
		this.handleModifyAssetsConfirm = this.handleModifyAssetsConfirm.bind(this);
		this.handleResetAssetsConfirm = this.handleResetAssetsConfirm.bind(this);
		this.handleReinitializeAssetsConfirm = this.handleReinitializeAssetsConfirm.bind(this);
		this.handleCloseModal = this.handleCloseModal.bind(this);
		this.handleAdjustFontSize = this.handleAdjustFontSize.bind(this);
	}

	public componentDidMount() {
		if (!Object.keys(this.props.files).length) {
			this.props.initializeAssets();
		}
		this.props.clearAuthIfExpired();
		this.props.renewAuth(this.props.user.authorization.authorizationToken);
		this.props.validateAssets();
	}

	public componentDidUpdate() {
		this.props.clearAuthIfExpired();
		renewAuthenticationIfAboutToExpire(this.props.user, this.props.renewAuth);
	}

	public render() {
		return <div className='assets'>
			<AssetStatus
				isPending={this.props.isPending}
				loadedOnce={this.props.loadedOnce}
				onOpenSite={this.props.openSite}
				onLoadAssets={this.handleLoadAssets}
				onModifyAssets={this.handleModifyAssets}
				onResetAssets={this.handleResetAssets}
				onReinitializeAssets={this.handleReinitializeAssets}
				onExportAssets={this.props.exportAssets}
				onImportAssets={this.handleImportAssets}
			/>
			<AssetLoadSuccessModal />
			<AssetLoadFailureModal />
			{this.renderContent()}
			{this.renderModifyAssetsModal()}
			{this.renderConfirmSwitchToAssetModal()}
			{this.renderConfirmLoadAssetsModal()}
			{this.renderConfirmResetAssetsModal()}
			{this.renderConfirmReinitializeAssetsModal()}
		</div>;
	}

	private renderContent() {
		return <SplitPaneVertical>
			<AssetList
				editingPath={this.getEditingPath()}
				paths={Object.keys(this.props.files)}
				errorState={this.props.validation}
				onClickFile={this.handleSwitchToAsset}
				fontSizeAdjustment={this.state.fontSizeAdjustment}
			/>
			{this.renderAssetView()}
		</SplitPaneVertical>;
	}

	private renderAssetView() {
		if (this.props.editing) {
			return <AssetEditor
				editing={this.props.editing}
				isModified={this.props.isModified}
				validationError={this.getValidationError()}
				onChange={this.props.changeAsset}
				onSave={this.handleSaveAsset}
				onRevert={this.handleRevertAsset}
				fontSizeAdjustment={this.state.fontSizeAdjustment}
				onAdjustFontSize={this.handleAdjustFontSize}
			/>;
		} else {
			return <EditorHelp showAsModal={false} />;
		}
	}

	private getValidationError() {
		// Necessary for backwards compatibility with old state without validation
		return this.props.validation && this.props.validation[this.props.editing.path];
	}

	private renderModifyAssetsModal() {
		return <ModifyAssetsModal
			isOpen={!!this.state.confirmModifyAssets}
			onClose={this.handleCloseModal}
			files={this.props.files}
			onModifyAssets={this.handleModifyAssetsConfirm}
		/>;
	}

	private renderConfirmSwitchToAssetModal() {
		return <ConfirmModal
			isOpen={!!this.state.confirmSwitchToAsset}
			confirmText='Switch'
			onConfirm={this.handleSwitchToAssetConfirm}
			onCancel={this.handleCloseModal}
		>
			<p>Are you sure you want to switch files and discard your updates?</p>
		</ConfirmModal>;
	}

	private renderConfirmLoadAssetsModal() {
		if (EXTERNAL_SERVICE_INTEGRATIONS_ENABLED) {
			return <ConfirmLoadAssetsModal
				isOpen={!!this.state.confirmLoadAssets}
				onDeploy={this.handleLoadAssetsConfirm}
				onCancel={this.handleCloseModal}
			/>;
		} else {
			return <ConfirmModal
				isOpen={!!this.state.confirmLoadAssets}
				confirmText='Deploy'
				onConfirm={this.handleLoadAssetsConfirm.bind(null, null)}
				onCancel={this.handleCloseModal}
			>
				<p>Are you sure you want to deploy your configuration?</p>
			</ConfirmModal>;
		}
	}

	private renderConfirmResetAssetsModal() {
		return <ConfirmModal
			isOpen={this.state.confirmResetAssets}
			confirmText='Revert'
			onConfirm={this.handleResetAssetsConfirm}
			onCancel={this.handleCloseModal}
		>
			<p>Are you sure you want to undo your changes and revert to the last deployed state?</p>
		</ConfirmModal>;
	}

	private renderConfirmReinitializeAssetsModal() {
		return <ConfirmModal
			isOpen={this.state.confirmReinitializeAssets}
			confirmText='Reset'
			onConfirm={this.handleReinitializeAssetsConfirm}
			onCancel={this.handleCloseModal}
		>
			Are you sure? Clicking "reset" will:
			<ul className='list-socotra-green'>
				<li>Discard your current configuration</li>
				<li>Import the default configuration</li>
				<li>Clear all data from your test instance</li>
			</ul>
		</ConfirmModal>;
	}

	private handleSwitchToAsset(path: string) {
		if (this.props.editing && this.props.editing.path === path) {
			return;
		}
		if (this.props.isModified) {
			this.setState({confirmSwitchToAsset: {path}});
		} else {
			this.props.switchToAsset(path);
		}
	}

	private handleLoadAssets() {
		this.setState({confirmLoadAssets: true});
	}

	private handleModifyAssets() {
		this.setState({confirmModifyAssets: true});
	}

	private handleResetAssets() {
		this.setState({confirmResetAssets: true});
	}

	private handleReinitializeAssets() {
		this.setState({confirmReinitializeAssets: true});
	}

	private handleSaveAsset() {
		this.props.saveAsset();
		this.props.validateAsset();
	}

	private handleRevertAsset() {
		this.props.revertAsset();
		this.props.validateAsset();
	}

	private handleImportAssets(zipFile: File) {
		this.props.importAssets(zipFile)
			.then(this.props.validateAssets);
	}

	private handleSwitchToAssetConfirm() {
		const path = this.state.confirmSwitchToAsset.path;
		this.handleCloseModal();
		this.props.switchToAsset(path);
	}

	private handleLoadAssetsConfirm(externalServiceIntegrations?: ExternalServiceIntegrationRequest[]) {
		if (externalServiceIntegrations) {
			this.handleCloseModal();
			this.props.loadAssets('UPDATE_TENANT', externalServiceIntegrations);
		} else {
			this.handleCloseModal();
			this.props.loadAssets('UPDATE_TENANT');
		}
	}

	private handleModifyAssetsConfirm(
		type: ConfigEditor.ModificationType,
		data: ModificationData,
		changeDisplay: ChangeModalDisplay,
	) {
		const {modifying, modification} = data;
		if (changeDisplay === 'CLOSE_MODAL') {
			this.handleCloseModal();
		}
		if (type === 'ADD_ASSETS') {
			this.props.addAssets(modifying, modification);
		} else {
			this.props.removeAssets(modifying, modification);
		}
		this.props.validateAssets();
	}

	private handleResetAssetsConfirm() {
		this.handleCloseModal();
		this.props.resetAssets();
		this.props.validateAssets();
	}

	private handleReinitializeAssetsConfirm() {
		this.handleCloseModal();
		this.props.initializeAssets();
		this.props.validateAssets();
	}

	private handleCloseModal() {
		this.setState({
			confirmSwitchToAsset: null,
			confirmLoadAssets: false,
			confirmModifyAssets: false,
			confirmResetAssets: false,
			confirmReinitializeAssets: false,
		});
	}

	private handleAdjustFontSize(adjustment: number) {
		this.setState({fontSizeAdjustment: adjustment});
	}

	private getEditingPath() {
		return this.props.editing ? this.props.editing.path : null;
	}

}

export default connect<StateProps, DispatchProps, {}>
	(mapStateToProps, mapDispatchToProps)(ConfigEditor);



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./ts/components/config-editor/index.tsx